import { window } from "global";
import { useEffect, useRef } from "react";
import classNames from "classnames";
import { useHistory } from "react-router-dom";
import { MediaQuery } from "@outplayed/responsive";
import { ChampionProfilePageError } from "@ugg/shared/components/Champions/ChampionProfilePageError";
import { ChampionRankingStatsNormal, ChampionRankingStatsMatchup } from "components/Champions/ChampionRankingStats";
import AdditionalStats from "components/Champions/AdditionalStats";
import Runes from "@ugg/shared/components/Champions/pages/Overview/Runes";
import SummonerSpells from "@ugg/shared/components/Champions/pages/Overview/SummonerSpells";
import Skills from "@ugg/shared/components/Champions/pages/Overview/Skills";
import ToughestMatchups from "@ugg/shared/components/Champions/pages/Overview/ToughestMatchups";
import Items from "@ugg/shared/components/Champions/pages/Overview/Items";
import ItemsRoleQuest from "@ugg/shared/components/Champions/pages/Overview/ItemsRoleQuest";
import RoleQuest from "@ugg/shared/components/Champions/pages/Overview/RoleQuest";
import AppDownloadButton from "components/Buttons/AppDownloadButton";
import { getRiotAssetsContext } from "@outplayed/riot-assets";
import { TooltipContainer } from "@outplayed/tooltips";
import BuildTabs from "@ugg/shared/components/Champions/pages/Overview/BuildTabs";
import { useChampionProfileContext } from "@ugg/shared/components/Champions/ChampionProfileContext";
import { useOverview, useBuildTabsRedirect } from "@ugg/shared/components/Champions/pages/Overview/overview-helpers";
import { capitalizeMulti } from "@ugg/shared/utils/multi-build-helpers";
import { ReactComponent as Warning } from "@ugg/shared/assets/svg/warning.svg";
import { ReactComponent as FeedbackIcon } from "@ugg/shared/assets/svg/feedback-icon.svg";
import { CHAMPION_PAGES } from "@ugg/shared/pages/champion-pages";
import { QueueTypeS } from "@ugg/shared/utils/queue-type-helpers";
import { RampInContentContainer } from "@ugg/shared/components/Ads/RampInContent";
import { useAdFree } from "lib/hooks/use-ad-free";
import { getAllstarChampHighlightUrl } from "@ugg/shared/api/requests/allstar/playlist";
import { PlusPromo } from "./CrossPromo";

import { AllstarEmbed } from "@ugitgud/legos/components/home-page/AllstarEmbed";

const PREROLL_AD_RATIO = 0.1;

const Build = () => {
  const history = useHistory();
  const { getChampionIdByName } = getRiotAssetsContext();
  const { page, championId, role, championData, multi } = useChampionProfileContext();
  const { filters, fetchingData, rankingStats, defaultChampionOverview, championOverview, tabData, championModifications } =
    useOverview({ ssr: true });
  const { opp, queueType, patch } = filters;
  const oppId = opp ? getChampionIdByName(opp) : 0;
  const championName = championData?.name || "";
  const buildTabRedirectUrl = useBuildTabsRedirect(championOverview);
  const isAdFree = useAdFree();
  const allStarHighlightUrl = getAllstarChampHighlightUrl(championName, championOverview, queueType);

  const shouldShowAds = useRef(Math.random() <= PREROLL_AD_RATIO);

  useEffect(() => {
    if (buildTabRedirectUrl) {
      history.replace(buildTabRedirectUrl);
    }
  }, [buildTabRedirectUrl]);

  const eventFire = (multi: string, championName: string, index: number) => {
    const build = capitalizeMulti(multi, multi);
    if (window.dataLayer) {
      window.dataLayer.push({
        event: "multibuild-tab-select",
        category: "filter",
        filterType: "multibuild",
        filterValue: build + "-" + championName + "-tab-" + (index + 1),
      });
    }
  };

  // Prevent flashing error state on client side
  // Skip on server to show error state
  if (window && buildTabRedirectUrl) {
    return null;
  }

  if (fetchingData) {
    return (
      <div className="champion-recommended-build">
        <div className="spinthatshit-loader">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  const {
    stat_shards,
    rec_runes,
    rec_summoner_spells,
    rec_starting_items,
    rec_core_items,
    rec_skills,
    rec_skill_path,
    rec_skill_evolve_path,
    item_options_1,
    item_options_2,
    item_options_3,
    item_options_4,
    t3_boots_options,
    role_quest_time,
  } = championOverview || {};

  const minDataExists =
    role && rec_runes && rec_summoner_spells && rec_starting_items && rec_core_items && rec_skills && rec_skill_path;
  // item_options_1 &&
  // item_options_2 &&
  // item_options_3;

  if (!minDataExists) {
    return <ChampionProfilePageError message="No data found. Please try again or change filters." />;
  }

  const tooltipInfo = (
    <div className="multibuilds-tooltip">
      <p>
        Multibuilds are new and still in BETA. We appreciate any and all feedback (
        <span>
          <FeedbackIcon style={{ display: "inline-block", height: "10px", width: "10px" }} />
        </span>
        ) as we continue to improve it.
      </p>
    </div>
  );

  return (
    <div className="champion-recommended-build ">
      <div className="flex flex-wrap gap-[12px] max-[700px]:flex-col">
        {oppId > 0 && defaultChampionOverview && (
          <ChampionRankingStatsMatchup className="flex-1" oppId={oppId} oppChampionOverview={defaultChampionOverview} />
        )}
        {oppId <= 0 && rankingStats && (
          <ChampionRankingStatsNormal
            className={classNames("flex-1", {
              "!grid-cols-[repeat(3,1fr)] ![&>*:nth-last-child(-n+3)]:row-start-2 !grid-flow-row": !!championModifications,
            })}
            colClassName={classNames({ "max-[700px]:!py-[12px]": !!championModifications })}
            rankingStats={rankingStats}
            queueType={queueType}
          />
        )}
        {championModifications && (
          <div className="relative flex-1">
            <div className="absolute max-[700px]:relative max-[700px]:max-h-[200px] top-0 left-0 right-0 bottom-0">
              <AdditionalStats className="h-full" queueType={queueType} championModifications={championModifications} />
            </div>
          </div>
        )}
      </div>
      <div className="rune-spell">
        {tabData && (
          <div className="flex h-[42px]">
            <div className="flex-1">
              <BuildTabs className="max-sm:hidden" version={patch} tabData={tabData} onClick={eventFire} />
            </div>
            <MediaQuery min="DESKTOP_SMALL" max="DESKTOP_LARGE">
              <AppDownloadButton
                className="flex !min-h-[34px] !h-[34px] rounded-[3px] !px-0 !py-[8px] text-center max-md:hidden"
                url={"/app?utm_source=uggbuild"}
                label="Auto-Import"
                icon={"cloud"}
              />
            </MediaQuery>
            <TooltipContainer tooltipInfo={tooltipInfo} customStyle={{ maxWidth: "400px" }} tooltipHoverable>
              <div className="flex items-center justify-center ml-[6px] w-[34px] h-[34px] bg-purple-400 rounded-[3px]">
                <Warning className="flex-none w-[18px] h-[18px]" />
              </div>
            </TooltipContainer>
          </div>
        )}
        <div className="content-section content-section_unset grid-1 max-sm:!flex max-sm:!flex-col">
          <Runes
            championName={championName}
            statShards={stat_shards}
            recRunes={rec_runes}
            patch={patch}
            role={role}
            multi={multi}
          />
          <SummonerSpells recSummonerSpells={rec_summoner_spells} />
          <MediaQuery min="DESKTOP_SMALL" max="DESKTOP_LARGE" renderNullOnFail>
            <PlusPromo className="max-sm:mt-[12px]" />
          </MediaQuery>
        </div>
      </div>
      {page === CHAMPION_PAGES.OVERVIEW && (
        <ToughestMatchups
          className="mt-[6px] md:mt-[12px]"
          championId={championId}
          role={role}
          matchups={rankingStats?.counters || []}
          multi={multi}
          queueType={queueType as QueueTypeS}
        />
      )}
      <Skills
        championId={championId}
        skillPriority={rec_skills}
        skillPath={rec_skill_path}
        evolutionPath={rec_skill_evolve_path}
      />
      <MediaQuery min="MOBILE_SMALL" max="MOBILE_LARGE">
        {!isAdFree && <RampInContentContainer className="mt-[12px]" />}
      </MediaQuery>
      <ItemsRoleQuest
        startingItems={rec_starting_items}
        coreItems={rec_core_items}
        items1={item_options_1 || []}
        items2={item_options_2 || []}
        items3={item_options_3 || []}
        items4={item_options_4 || []}
        t3Boots={t3_boots_options || []}
        patch={patch}
        role={role}
      />
      <div className="flex mt-3 gap-[1px]">
        <RoleQuest
          className="flex-1"
          layout="column"
          champID={championId}
          questCompletionTime={role_quest_time}
          role={role}
          queueType={queueType as QueueTypeS}
        />
        <MediaQuery min="DESKTOP_SMALL" max="DESKTOP_LARGE" renderNullOnFail>
          <AllstarEmbed path={allStarHighlightUrl} width={441} height={300} layout="nextUp" enableAds={shouldShowAds.current} />
        </MediaQuery>
      </div>
    </div>
  );
};

export default Build;
